import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { samples as samplesApi } from '../utils/api';
import { useT } from '../i18n/I18nContext';

const STATUS_COLOR = {
  'Order Placed':      { bg: '#EBF8FF', color: '#2B6CB0' },
  'Sample Sent':       { bg: '#FFFBEB', color: '#92400E' },
  'Sample Approved':   { bg: '#F0FFF4', color: '#276749' },
  'Rejected':          { bg: '#FFF5F5', color: '#C53030' },
};

const ALERT_META = {
  customs_hold:    { bg: '#FFF3E0', color: '#E65100', icon: '📦' },
  customs_action:  { bg: '#FFF9C4', color: '#F57F17', icon: '⚠️' },
  payment_due:     { bg: '#FCE4EC', color: '#AD1457', icon: '💰' },
};

export default function Samples() {
  const t = useT();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate              = useNavigate();

  useEffect(() => {
    samplesApi.list()
      .then(data => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const total      = rows.length;
  const inTransit  = rows.filter(r => r.status === 'Sample Sent').length;
  const received   = rows.filter(r => ['Sample Approved', 'Rejected'].includes(r.status)).length;
  const approved   = rows.filter(r => r.status === 'Sample Approved').length;
  const approvalRate = received > 0 ? Math.round(approved / received * 100) : 0;

  const hasAlerts = rows.some(r => r.tracking_alert_type);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">🧪 {t('samples.title')}</h1>
          <p className="page-subtitle">{t('samples.subtitle')}</p>
        </div>
      </div>

      {hasAlerts && (
        <div style={{ marginBottom: 16 }}>
          {rows.filter(r => r.tracking_alert_type).map(r => {
            const meta = ALERT_META[r.tracking_alert_type] || ALERT_META.customs_hold;
            return (
              <div key={r.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 16px', borderRadius: 'var(--radius)',
                background: meta.bg, color: meta.color,
                border: `1.5px solid ${meta.color}33`, marginBottom: 8,
              }}>
                <span style={{ fontSize: 18 }}>{meta.icon}</span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {r.reference} — {t(`tracking.alert.${r.tracking_alert_type}`)}
                </span>
                {r.tracking_alert_link && (
                  <a href={r.tracking_alert_link} target="_blank" rel="noreferrer"
                    style={{ marginLeft: 'auto', fontSize: 12, color: meta.color, fontWeight: 700 }}>
                    {t('tracking.alert.action_link')}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:16, marginBottom:24 }}>
        {[
          { label: t('samples.kpi.total'),         value: total,              icon:'🧪' },
          { label: t('samples.kpi.in_transit'),    value: inTransit,          icon:'✈️' },
          { label: t('samples.kpi.received'),      value: received,           icon:'📦' },
          { label: t('samples.kpi.approval_rate'), value:`${approvalRate}%`,  icon:'✅' },
        ].map(k => (
          <div key={k.label} className="card" style={{ textAlign:'center', padding:'20px 16px' }}>
            <div style={{ fontSize:28, marginBottom:8 }}>{k.icon}</div>
            <div style={{ fontSize:28, fontWeight:700, color:'var(--pantone)' }}>{k.value}</div>
            <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--gray-400)' }}>{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--gray-400)' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🧪</div>
            <p>{t('samples.empty')}</p>
            <p style={{ fontSize:13 }}>{t('samples.empty.sub')}</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('samples.col.reference')}</th>
                  <th>{t('samples.col.supplier')}</th>
                  <th>{t('samples.col.items')}</th>
                  <th>{t('samples.col.tracking')}</th>
                  <th>{t('samples.col.live_status')}</th>
                  <th>{t('samples.col.edd')}</th>
                  <th>{t('samples.col.dispatched')}</th>
                  <th>{t('samples.col.received')}</th>
                  <th>{t('samples.col.approved')}</th>
                  <th>{t('col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const style = STATUS_COLOR[r.status] || { bg:'var(--gray-100)', color:'var(--gray-600)' };
                  const approvedCount = r.approved_items || 0;
                  const totalItems    = r.total_items    || 0;
                  const alertMeta     = r.tracking_alert_type ? ALERT_META[r.tracking_alert_type] : null;
                  return (
                    <tr key={r.id} style={{ cursor:'pointer' }}
                      onClick={() => r.order_type === 'sample'
                        ? navigate(`/orders/${r.id}?from=samples`)
                        : navigate(`/quotations/${r.id}?tab=samples&from=samples`)}>
                      <td>
                        <span style={{ fontFamily:'var(--font-mono)', color:'var(--pantone)', fontWeight:600 }}>
                          {r.reference}
                        </span>
                        {alertMeta && (
                          <span title={r.tracking_alert_message} style={{ marginLeft: 6, cursor: 'help' }}>
                            {alertMeta.icon}
                          </span>
                        )}
                      </td>
                      <td>{r.supplier_name}</td>
                      <td style={{ textAlign:'center' }}>{totalItems}</td>
                      <td>
                        {r.tracking_number ? (
                          <span style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>
                            {r.carrier ? `${r.carrier} · ` : ''}{r.tracking_number}
                          </span>
                        ) : <span style={{ color:'var(--gray-400)', fontSize:12 }}>—</span>}
                      </td>
                      <td>
                        {r.tracking_live_status ? (
                          <span style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                            background: alertMeta ? alertMeta.bg : '#EBF8FF',
                            color: alertMeta ? alertMeta.color : '#2B6CB0',
                          }}>
                            {r.tracking_live_status}
                          </span>
                        ) : r.tracking_number ? (
                          <span style={{ fontSize:11, color:'var(--gray-400)' }}>—</span>
                        ) : <span style={{ color:'var(--gray-300)', fontSize:11 }}>—</span>}
                      </td>
                      <td style={{ fontSize:12 }}>
                        {r.tracking_edd ? r.tracking_edd.slice(0,10) : '—'}
                      </td>
                      <td style={{ fontSize:12 }}>{r.dispatched_at ? r.dispatched_at.slice(0,10) : '—'}</td>
                      <td style={{ fontSize:12 }}>{r.received_at ? r.received_at.slice(0,10) : '—'}</td>
                      <td style={{ textAlign:'center' }}>
                        {totalItems > 0 ? (
                          <span style={{ fontSize:12, fontWeight:600, color: approvedCount === totalItems ? '#276749' : 'var(--gray-600)' }}>
                            {approvedCount}/{totalItems}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        <span style={{ ...style, padding:'3px 10px', borderRadius:12, fontSize:12, fontWeight:500 }}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
